/**
 * Global fetch interceptor — attaches `Authorization: Bearer <id_token>`
 * to every same-origin `/api/*` request automatically.
 *
 * MUST be called BEFORE the first React render — i.e. before
 * createRoot(...).render(...) in src/main.tsx. Otherwise the very first
 * /api/auth/me call from useAuth fires without auth and is rejected
 * with 401.
 */

import { authToken } from './authToken';

const PATCH_KEY = Symbol.for('8x8.authFetch.patched');

interface PatchedWindow {
  [PATCH_KEY]?: boolean;
  fetch: typeof fetch;
}

export function installAuthFetch() {
  if (typeof window === 'undefined') return;
  const w = window as unknown as PatchedWindow;
  if (w[PATCH_KEY]) return;

  const original = w.fetch.bind(window);

  w.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (!shouldAttachAuth(input)) return original(input, init);

    const token = authToken.current();
    if (!token) return original(input, init);

    const incoming = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    if (!incoming.has('Authorization')) {
      incoming.set('Authorization', `Bearer ${token}`);
    }

    if (input instanceof Request) {
      const reissued = new Request(input, { headers: incoming });
      return original(reissued, init);
    }
    return original(input, { ...init, headers: incoming });
  };

  w[PATCH_KEY] = true;
}

function shouldAttachAuth(input: RequestInfo | URL): boolean {
  let url: string;
  if (typeof input === 'string') {
    url = input;
  } else if (input instanceof URL) {
    url = input.toString();
  } else if (input instanceof Request) {
    url = input.url;
  } else {
    return false;
  }

  if (url.startsWith('/api/')) return true;

  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin && parsed.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}
